import { useCallback, useEffect, useMemo, useState } from 'react';

import * as qs from 'query-string';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { PageContainer } from 'components';
import { ROUTES } from 'core';
import { useLazyGetOrganisationCountryConfigByDistrictIdQuery } from 'store/my-registrations/myRegistrationsAPI';
import { StartUserMigrationModal } from 'components/UserMigration/StartUserMigrationModal';
import { MinorToAdultMigrationStatusModal } from 'components/UserMigration/MinorToAdultMigrationStatusModal';
import { useLazyChildRequireMigrationQuery } from 'store/candidate/candidateAPI';

import { Button } from '@britishcouncil/react-solas';
import { BcSelect, Divider, LinkButton, MainContainer } from '@britishcouncil/react-solas-ors3';

import { useMyRegistrationsData } from '../my-registrations/useMyRegistrationsData';

import { CandidateDetailsInfoModal } from './components/CandidateDetailsInfoModal';
import { CandidateForm } from './components/CandidateForm';
import { ReadonlyCandidate } from './components/ReadonlyCandidate';

export const MyProfile = () => {
  const { showMigrationAvailable } = qs.parse(useLocation().search, { parseBooleans: true, parseNumbers: false });

  const navigate = useNavigate();
  const { isLoading, data: summary, refreshCandidateSummary } = useMyRegistrationsData();
  const [isEditMode, setEditMode] = useState(false);
  const [showInfoModal, setInfoModal] = useState(false);
  const [getOrganisationCountryConfig, { data: districtConfig, isLoading: isConfigLoading }] =
    useLazyGetOrganisationCountryConfigByDistrictIdQuery();
  const [selectedCandidateId, setCandidateId] = useState<string | undefined>(undefined);

  const candidates = useMemo(() => (summary ? [summary.candidate, ...summary.childCandidates] : []), [summary]);

  const candidateOptions = useMemo(
    () => candidates.map((x) => ({ value: x.id, name: `${x.otherNames} ${x.familyName}` })),
    [candidates]
  );

  const selectedCandidate = useMemo(
    () => candidates.find((x) => selectedCandidateId && x.id === Number(selectedCandidateId)),
    [candidates, selectedCandidateId]
  );

  const allowAmendments = districtConfig && districtConfig.allowAmendments;

  const handleUpdateSuccess = (emailOrNameChanged: boolean) => {
    setEditMode(false);
    refreshCandidateSummary();
    if (emailOrNameChanged) {
      navigate(ROUTES.LOGOUT);
    }
  };

  useEffect(() => {
    if (summary && summary.candidate.id) {
      if (showMigrationAvailable) {
        let childWithAvailableMigration = summary.childCandidates.filter(
          (x) => x.isMinorToAdultMigrationAvailable && !x.isMinorToAdultMigrationInProgress
        );
        if (childWithAvailableMigration.length === 0) {
          setCandidateId(`${summary.candidate.id}`);
        } else {
          setCandidateId(`${childWithAvailableMigration[0].id}`);
        }
      } else {
        setCandidateId(`${summary.candidate.id}`);
      }

      //TODO[GP - future fix] - we should find better way to fetch organisation config....
      const districtId = summary.exams[0]?.districtId;
      if (districtId) {
        getOrganisationCountryConfig(districtId);
      }
    }
  }, [summary, getOrganisationCountryConfig, showMigrationAvailable]);

  const [showMigrationAccountModal, setShowMigrationAccountModal] = useState(false);
  const [showMigrationStatusModal, setShowMigrationStatusModal] = useState(false);

  let { shouldMigrateSelectedCandidate, migrationIsInProgress } = useMemo(
    () => ({
      shouldMigrateSelectedCandidate: selectedCandidate?.isMinorToAdultMigrationAvailable === true,
      migrationIsInProgress: selectedCandidate?.isMinorToAdultMigrationInProgress === true,
    }),
    [selectedCandidate]
  );

  const [triggerChildRequireMigrationQuery] = useLazyChildRequireMigrationQuery();

  const closeMigrationModal = useCallback(
    (migrationStartedSuccess: boolean) => {
      setShowMigrationAccountModal(false);

      if (migrationStartedSuccess) {
        refreshCandidateSummary();
        triggerChildRequireMigrationQuery();
      }
    },
    [refreshCandidateSummary, setShowMigrationAccountModal, triggerChildRequireMigrationQuery]
  );

  const closeCancelMigrationModal = useCallback(
    (cancelledSuccessfully: boolean) => {
      setShowMigrationStatusModal(false);

      if (cancelledSuccessfully) {
        refreshCandidateSummary();
        triggerChildRequireMigrationQuery();
      }
    },
    [refreshCandidateSummary, setShowMigrationStatusModal, triggerChildRequireMigrationQuery]
  );

  return (
    <>
      <MainContainer>
        <h1>My profile</h1>
      </MainContainer>

      <PageContainer loading={isLoading || isConfigLoading}>
        {candidateOptions.length > 1 ? (
          <>
            <label htmlFor="candidate-select">Candidate</label>
            <BcSelect
              id="candidate-select"
              value={selectedCandidateId}
              onChange={(_, option) => {
                setEditMode(false);
                setCandidateId(option.value);
              }}
              options={candidateOptions}
            ></BcSelect>
          </>
        ) : (
          <h2>
            {summary?.candidate.otherNames} {summary?.candidate.familyName}
          </h2>
        )}
        {selectedCandidate && (
          <>
            {migrationIsInProgress && (
              <DivCenteredItems>
                <Button style={{ marginTop: '10px' }} onClick={() => setShowMigrationStatusModal(true)}>
                  Migration status
                </Button>
                <MinorToAdultMigrationStatusModal
                  candidateId={selectedCandidate.id}
                  email={selectedCandidate.email}
                  onExit={closeCancelMigrationModal}
                  show={showMigrationStatusModal}
                  startedBy={selectedCandidate.minorToAdultMigrationStartedBy}
                  startedOn={selectedCandidate.minorToAdultMigrationStartedOn}
                />
              </DivCenteredItems>
            )}
            {shouldMigrateSelectedCandidate && (
              <DivCenteredItems>
                <Button style={{ marginTop: '10px' }} onClick={() => setShowMigrationAccountModal(true)}>
                  Migrate Account
                </Button>
                <StartUserMigrationModal
                  show={showMigrationAccountModal}
                  onExit={closeMigrationModal}
                  candidateId={selectedCandidate.id}
                />
              </DivCenteredItems>
            )}
            <Divider />

            {!isEditMode ? (
              <ReadonlyCandidate candidate={selectedCandidate} />
            ) : (
              <CandidateForm
                candidate={selectedCandidate}
                onCancel={() => setEditMode(false)}
                onSuccess={handleUpdateSuccess}
              />
            )}

            {allowAmendments && !isEditMode && (
              <>
                <Button onClick={() => setEditMode(true)} disabled={summary?.isCandidateDetailsDisabled}>
                  Edit
                </Button>
                {summary?.isCandidateDetailsDisabled && (
                  <div>
                    <LinkButton
                      onClick={() => {
                        setInfoModal(true);
                      }}
                    >
                      Why can't edit details?
                    </LinkButton>
                    <CandidateDetailsInfoModal onExit={() => setInfoModal(false)} show={showInfoModal} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
};

const DivCenteredItems = styled.div`
  text-align: center;
`;
